/* Sticky buy bar.

   Shows itself only once the real Add to cart button has scrolled out of the
   viewport, and hides again when it comes back. Clicking it re-submits the real
   product form, so the upsell modal and cart drawer run their normal course —
   this file never touches /cart/add.js itself.
*/
(function () {
  if (customElements.get('dbp-stickybar')) return;

  class DbpStickyBar extends HTMLElement {
    connectedCallback() {
      // PORTAL TO <body> FIRST.
      // The bar renders inside .product__info-container. `position: fixed` is
      // resolved against the nearest ancestor that has transform / filter /
      // perspective / will-change / contain — and any of those can arrive later
      // from an app script, an animation on a wrapper, or a preview toolbar.
      // When one does, the bar stops being pinned to the viewport and parks at
      // that ancestor's bottom edge instead: it appears halfway down the page
      // and scrolls away with it. Moving the element to <body> removes every
      // intermediate ancestor, so the only thing that could still capture it is
      // <body> itself. Same fix as dbp-upsell.
      if (this.parentElement !== document.body) {
        // A portaled node no longer lives inside the section it came from, so a
        // section re-render (every save in the theme editor) leaves the old one
        // parked on <body> and we end up with two stacked bars. Clear any
        // previous instance before parking this one.
        document.querySelectorAll('dbp-stickybar').forEach((el) => {
          if (el !== this && el.parentElement === document.body) el.remove();
        });
        document.body.appendChild(this); // re-fires connectedCallback
        return;
      }
      if (this.initialised) return;
      this.initialised = true;

      this.btn = this.querySelector('[data-sticky-add]');
      this.form =
        document.querySelector('product-form form[action*="/cart/add"]') ||
        document.querySelector('form[action*="/cart/add"]');
      this.anchor = this.form && this.form.querySelector('button[name="add"]');

      // No buy form on the page (or no button to watch) — stay out of the way.
      if (!this.btn || !this.form || !this.anchor) {
        this.remove();
        return;
      }

      this.btn.addEventListener('click', () => this.submit());

      this.observeAnchor();
      this.observeSuppressors();
      this.observePrice();
    }

    disconnectedCallback() {
      if (this.onScroll) {
        window.removeEventListener('scroll', this.onScroll);
        window.removeEventListener('resize', this.onScroll);
      }
      this.mo && this.mo.disconnect();
    }

    /* --- visibility ------------------------------------------------------ */

    observeAnchor() {
      // Deliberately a scroll listener, not IntersectionObserver. IO only fires
      // on a threshold crossing, and the button can go from below the viewport
      // straight to above it — on a phone the gallery fills the first screen, so
      // the button starts off-screen — without ever intersecting. That produces
      // no callback at all and the bar never appears. One getBoundingClientRect
      // per scroll event on a single element is cheap and always correct.
      this.onScroll = () => {
        const r = this.anchor.getBoundingClientRect();
        // Show only once the button has fully left the top of the screen: the
        // shopper has read the buy box and moved past it. While it is still
        // below the fold they are on their way to it, and a floating duplicate
        // of a button they have not reached yet is just noise.
        this.setVisible(r.bottom < 0);
      };
      window.addEventListener('scroll', this.onScroll, { passive: true });
      window.addEventListener('resize', this.onScroll, { passive: true });
      this.onScroll();
    }

    setVisible(on) {
      this.classList.toggle('is-visible', on);
      this.setAttribute('aria-hidden', on ? 'false' : 'true');
      // Keeps the bar off the tab order while it is off-screen.
      if (on) this.removeAttribute('inert');
      else this.setAttribute('inert', '');
    }

    /* The upsell modal and the cart drawer both own the screen when open. */
    observeSuppressors() {
      const targets = [
        document.querySelector('dbp-upsell'),
        document.querySelector('cart-drawer'),
      ].filter(Boolean);
      if (!targets.length) return;

      const check = () => {
        const busy = targets.some(
          (t) => t.classList.contains('is-open') || t.classList.contains('active')
        );
        this.classList.toggle('is-suppressed', busy);
      };

      this.mo = new MutationObserver(check);
      targets.forEach((t) =>
        this.mo.observe(t, { attributes: true, attributeFilter: ['class'] })
      );
      check();
    }

    /* Packs are radios inside the buy form and each carries its own total, so
       the checked label is the single source of truth for what the bar shows.
       Falls back to the theme price element when the pack selector is off. */
    observePrice() {
      this.now = this.querySelector('[data-sticky-price]');
      this.was = this.querySelector('[data-sticky-was]');
      if (!this.now) return;

      this.syncPrice();
      this.form.addEventListener('change', (e) => {
        const t = e.target;
        if (t && t.name === 'id') this.syncPrice();
        else if (t && t.name === 'quantity') this.syncPrice();
      });
    }

    syncPrice() {
      const checked = this.form.querySelector(
        'input[name="id"]:checked, input[name="quantity"]:checked'
      );
      const label = checked && checked.closest('.dbp-pack');
      if (!label) return;

      const now = label.querySelector('.dbp-pack__now');
      const was = label.querySelector('.dbp-pack__was');

      if (now && now.textContent.trim()) this.now.textContent = now.textContent.trim();

      if (this.was) {
        if (was && was.textContent.trim()) {
          this.was.textContent = was.textContent.trim();
          this.was.hidden = false;
        } else {
          // Quantity packs carry no compare-at, so the struck price would be a
          // stale single-bottle figure sitting next to a multi-bottle total.
          this.was.hidden = true;
        }
      }
    }

    /* --- action ---------------------------------------------------------- */

    submit() {
      if (this.btn.disabled) return;
      // Hand straight back to the real form: its submit handler owns the upsell
      // modal, the AJAX add and the drawer refresh.
      if (typeof this.form.requestSubmit === 'function') this.form.requestSubmit();
      else this.form.dispatchEvent(new Event('submit', { cancelable: true, bubbles: true }));
    }
  }

  customElements.define('dbp-stickybar', DbpStickyBar);
})();
